import * as React from "react";
import {FontAwesomeIcon}        from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronUp, faRotate} from "@fortawesome/free-solid-svg-icons";

import "./field-results-card.scss";

import {cs}               from "@Common/react/chain-services";
import {UseState}         from "@Common/react/use-state";
import {consumeContext}   from "@Common/react/context";
import {ScreenLoader}     from "@Common/ui-components/loading-spinners/screen-loader";
import {SuggestionResult} from "./suggestion-result";
import {WizardTool}       from "./wizard-tool";
import {isDevXd}          from "@Common/ui-components/debug/is-dev";

export const FieldResultsCard = ({idOrder, item, field, initFieldSuggestions, propertyWizardInsertValues}) => cs(
    consumeContext("apis"),
    ["fieldSuggestions", ({}, next) => UseState({
        _key: initFieldSuggestions,
        initValue: initFieldSuggestions,
        next
    })],
    ["loading", ({fieldSuggestions}, next) => UseState({
        _key: fieldSuggestions,
        initValue: !fieldSuggestions.value?.length || fieldSuggestions.value[0]?.error,
        next
    })],
    ["fieldIsExpanded", ({}, next) => UseState({
        initValue: true,
        next
    })],
    ["mapSuggestionToComponent", ({fieldSuggestions, loading}, next) => next((suggestion) => {
        return (
            <SuggestionResult
                idOrder={idOrder}
                item={item}
                field={field}
                suggestion={suggestion}
                fieldSuggestions={fieldSuggestions}
                loading={loading}
                propertyWizardInsertValues={propertyWizardInsertValues}
            />
        )
    })],
    ["reloadCurrentFieldSuggestions", ({apis, loading, fieldSuggestions}, next) => next(async () => {
        try {
            loading.onChange(true);
            fieldSuggestions.onChange([]);
            let timeout, failureValue="ERROR: timeout", timeLimit=26000;
            const timeoutPromise = new Promise((resolve, reject) => {
                timeout = setTimeout(() => {
                    resolve(failureValue);
                }, timeLimit);
            });
            let task = apis.next.gpt.generateFieldSuggestions({
                idOrder: parseInt(idOrder),
                numOfSuggestions: 3,
                idProductType: parseInt(item.idProductType),
                field: {
                    idPrimary: field.idPrimary,
                    fieldKey: field.fieldKey,
                    displayLabel: field.displayLabel,
                    maxLength: field.maxLength,
                    enableLimitOverride: field.enableLimitOverride,
                },
            });
            let results = await Promise.race([task, timeoutPromise]);
            if(results.error || typeof results == "string") {
                isDevXd() && console.error(`Error processing field ${field.displayLabel}:`);
                fieldSuggestions.onChange([{
                    content : "",
                    displayLabel : field.displayLabel,
                    finishReason : "error",
                    idPrimary : field.idPrimary,
                    error: "Failed to generate suggestions",
                }]);
                loading.onChange(false);
            } else {
                fieldSuggestions.onChange(results);
            }
        } catch (error) {
            console.error("Error fetching property wizard data:", error);
            fieldSuggestions.onChange([{
                content : "",
                displayLabel : field.displayLabel,
                finishReason : "error",
                idPrimary : field.idPrimary,
                error: "Failed to generate suggestions",
            }]);
            loading.onChange(false);
        }
    })],

    ({loading, fieldSuggestions, fieldIsExpanded, mapSuggestionToComponent, reloadCurrentFieldSuggestions}) => (
        <div className="field-results-card-j63">
            {fieldSuggestions.value[0]?.error ?
                <div className="error-generating-suggestions">
                    <p>Error generating suggestions.</p>
                    <p> Please try again.</p>
                    <div className="retry-generate-suggestions"
                        onClick={() => {reloadCurrentFieldSuggestions()}}
                    >
                        Retry
                    </div>
                </div>
            : null}
            <div className="field-results-card-header">
                <div className="field-label-and-reload">
                    {field.displayLabel}
                    <WizardTool
                        tooltipCopy="Regenerate"
                        onClickFunction={reloadCurrentFieldSuggestions}
                    >
                        <FontAwesomeIcon icon={faRotate}/>
                    </WizardTool>
                </div>
                <div className="field-expand-collapse">
                    <span className="down-chevron">
                        {fieldIsExpanded.value ?
                            <FontAwesomeIcon icon={faChevronUp}
                                onClick={() => fieldIsExpanded.onChange(false)}
                            />
                        :
                            <FontAwesomeIcon icon={faChevronDown}
                                onClick={() => fieldIsExpanded.onChange(true)}
                            />
                        }
                    </span>
                </div>
            </div>
            <div className={fieldIsExpanded.value ? "content" : "content collapsed"}>
                {loading.value && !fieldSuggestions.value[0]?.error ?
                    <div className="page-loader"><ScreenLoader loaderColor="#6937B7"/></div>
                :
                    fieldSuggestions.value?.map((suggestion) => mapSuggestionToComponent(suggestion))
                }
            </div>
        </div>
    )
);